// /**
//  * Parcels details - list
//  */

// React
import React from 'react';

import { Link } from 'react-router-dom';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiCheckBoxOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiFileDocumentOutline,
  mdiSquareOutline,
  mdiClockAlertOutline,
  mdiDownload,
} from '@mdi/js';

// Helper
import { checkMatchMedia, translate } from '../../utils/helpers';
import jMoment from 'moment-jalaali';

// Type
import { ItemDetail } from '../parcels/interfaces';
import theme from '../../theme';
import stableSort from '../../utils/Table/stableSort';
import getComparator from '../../utils/Table/getComparator';

type Data = ItemDetail;
type Props = {
  data: any;
  changeStatus: (status: any) => void;
  handleOpenExpirationDateModal: (passedData: any) => void;
  selectAll: () => void;
  checkedAll: boolean;
  downloadImage: (id:number, name: string) => void;
};
type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
  selectAll: () => void;
  checked: boolean;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      whiteSpace: 'nowrap',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      whiteSpace: 'nowrap',
      fontSize: 14,
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  })
);

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const { order, orderBy, onRequestSort, selectAll, checked } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  let headCells: HeadCell[] = [
    { id: 'status', label: 'وضعیت' },
    { id: 'productName', label: 'نام کالا' },
    { id: 'productCode', label: 'کد کالا' },
    { id: 'skuId', label: 'شناسه تنوع کالا' },
    { id: 'variantCode', label: 'کد تنوع کالا' },
    { id: 'count', label: 'تعداد کالا' },
    { id: 'parcelItemProdDate', label: 'تاریخ تولید' },
    { id: 'parcelItemExpDate', label: 'تاریخ انقضاء' },
    { id: 'updateDateSection', label: '    ' },
  ];

  return (
    <TableHead>
      <StyledTableRow>
        <StyledTableCell>
          <IconButton
            aria-label="details"
            size="small"
            title="وضعیت"
            data-cy="details"
            onClick={() => selectAll()}
          >
            <Icon
              path={
                checked
                  ? mdiCheckBoxOutline
                  : mdiSquareOutline
              }
              size={1}
              color={"#fff"}
            />
          </IconButton>
        </StyledTableCell>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('parcelId');
  // const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
              selectAll={props.selectAll}
              checked={props.checkedAll}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  let variantName = row.productName;
                  if (row.featureAndValues) {
                    for (let i = 0; i <= 2 && row?.featureAndValues[i]; i++) {
                      variantName = variantName.concat(
                        ' - ',
                        row.featureAndValues[i].key,
                        ' : ',
                        row.featureAndValues[i].value
                      );
                    }
                  }

                  if (row.sellerName || row.providerName) {
                    const sellerName = row.sellerName ?? row.providerName;
                    variantName = variantName.concat(' ( ', sellerName, ' )');
                  }

                  return (
                    <StyledTableRow key={index} hover tabIndex={-1}>
                      <TableCell>
                        <IconButton
                          aria-label="details"
                          size="small"
                          title="وضعیت"
                          data-cy="details"
                          onClick={() => props.changeStatus(row)}
                        >
                          <Icon
                            path={
                              row.status === 'UNLOADED'
                                ? mdiCheckBoxOutline
                                : mdiSquareOutline
                            }
                            size={1}
                            color={theme.palette.green.main}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {translate(row.status, 'parcel')}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {variantName}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.productCode}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.skuId}
                      </TableCell>
                      <TableCell align="center" data-cy="row-id">
                        {row.variantCode}
                      </TableCell>
                      <TableCell align="center" data-cy="row-id">
                        {row.count}
                      </TableCell>
                      <TableCell align="center">
                        {row.inventoryItems[0].productionDate
                          ? `${jMoment(
                            row.inventoryItems[0].productionDate,
                            'YYYY-MM-DD'
                          )
                            .locale('fa')
                            .format('jYYYY/jMM/jDD')}`
                          : `نامشخص`}
                      </TableCell>
                      <TableCell align="center">
                        {row.inventoryItems[0].expirationDate
                          ? `${jMoment(
                            row.inventoryItems[0].expirationDate,
                            'YYYY-MM-DD'
                          )
                            .locale('fa')
                            .format('jYYYY/jMM/jDD')}`
                          : `نامشخص`}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{ display: '', alignItems: 'stretch' }}
                      >
                        {row.productImageUrl && (
                          <a href={row.productImageUrl} target="_blank">
                            <img
                              src={row.productImageUrl}
                              alt="image"
                              loading="lazy"
                              width="30"
                              height="30"
                            />
                          </a>
                        )}
                        <Link to={`/parcel-item/details/${row.id}`}>
                          <IconButton
                            aria-label="details"
                            size="small"
                            title="جزئیات آیتم محموله"
                            data-cy="details"
                          >
                            <Icon
                              path={mdiFileDocumentOutline}
                              size={1}
                              color={theme.palette.green.main}
                            />
                          </IconButton>
                        </Link>
                        <IconButton
                          aria-label="update-date"
                          onClick={() =>
                            props.handleOpenExpirationDateModal(row)
                          }
                        >
                          <Icon
                            path={mdiClockAlertOutline}
                            size={1}
                            color={theme.palette.green.main}
                          />
                        </IconButton>
                        {row.productImageUrl && <a href={row.productImageUrl} target="_blank">
                          <img width="30" height="33" src={row.productImageUrl} />
                        </a>}
                        {row.productImageFileName && <IconButton onClick={() => props.downloadImage(row.productId, row.productImageFileName)}>
                        <Icon path={mdiDownload} size={1}/>  
                      </IconButton>}
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
